import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Search, CheckCircle2, X, Ban, RotateCcw, Edit3, Loader2 } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_TABS = [
    { value: "pending_approval", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "suspended", label: "Suspended" },
    { value: "", label: "All" },
];

const STATUS_TONE = {
    pending_approval: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    suspended: "bg-zinc-200 text-zinc-700",
};

const TIERS = [
    { value: "tier1", label: "Tier 1 — 5% off (Clinics)" },
    { value: "tier2", label: "Tier 2 — 10% off (Pharmacies)" },
    { value: "tier3", label: "Tier 3 — 15% off (Hospitals)" },
];

const TERMS = [
    { value: 0, label: "Prepaid only" },
    { value: 15, label: "Net 15" },
    { value: 30, label: "Net 30" },
];

const AdminB2BAccounts = () => {
    const [tab, setTab] = useState("pending_approval");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null); // selected account doc for drawer
    const [mode, setMode] = useState(null); // approve | reject | suspend | edit
    const [form, setForm] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (tab) params.status = tab;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/b2b/accounts", { params });
            setItems(data.items || []);
            setCounts(data.counts || {});
        } finally {
            setLoading(false);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab]);

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/admin/b2b/accounts/${id}`);
            setActive(data);
            setMode(null);
        } catch {
            toast.error("Failed to load account");
        }
    };

    const openMode = (m) => {
        if (!active) return;
        setMode(m);
        if (m === "approve") {
            setForm({
                credit_limit: active.credit_limit || 100000,
                credit_terms_days: active.credit_terms_days || 15,
                discount_tier: active.discount_tier || "tier1",
                has_hospital_certificate: active.has_hospital_certificate || false,
            });
        } else if (m === "edit") {
            setForm({
                credit_limit: active.credit_limit || 0,
                credit_terms_days: active.credit_terms_days || 15,
                discount_tier: active.discount_tier || "tier1",
                has_hospital_certificate: active.has_hospital_certificate || false,
            });
        } else if (m === "reject" || m === "suspend") {
            setForm({ reason: "" });
        }
    };

    const submit = async () => {
        if (!active || !mode) return;
        setSubmitting(true);
        try {
            const id = active.id;
            if (mode === "approve") {
                await api.post(`/admin/b2b/accounts/${id}/approve`, form);
                toast.success("Account approved");
            } else if (mode === "reject") {
                if (!form.reason?.trim()) { toast.error("Reason required"); return; }
                await api.post(`/admin/b2b/accounts/${id}/reject`, form);
                toast.success("Account rejected");
            } else if (mode === "suspend") {
                await api.post(`/admin/b2b/accounts/${id}/suspend`, form);
                toast.success("Account suspended");
            } else if (mode === "edit") {
                await api.patch(`/admin/b2b/accounts/${id}`, form);
                toast.success("Account updated");
            }
            setMode(null);
            setActive(null);
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || d?.code || "Action failed");
        } finally {
            setSubmitting(false);
        }
    };

    const reactivate = async () => {
        if (!active) return;
        if (!window.confirm("Reactivate this account?")) return;
        setSubmitting(true);
        try {
            await api.post(`/admin/b2b/accounts/${active.id}/reactivate`);
            toast.success("Account reactivated");
            setActive(null);
            await load();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout title="B2B accounts" subtitle="Wholesale clinic & hospital approvals">
                <div className="space-y-5" data-testid="admin-b2b-accounts">
                    <div className="flex flex-wrap items-center gap-2">
                        {STATUS_TABS.map((t) => (
                            <button
                                key={t.value || "all"}
                                onClick={() => setTab(t.value)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                    tab === t.value
                                        ? "bg-brand-700 text-white"
                                        : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                                }`}
                                data-testid={`admin-b2b-tab-${t.value || "all"}`}
                            >
                                {t.label}
                                {counts[t.value] != null && t.value && (
                                    <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${tab === t.value ? "bg-white/20" : "bg-zinc-100"}`}>{counts[t.value]}</span>
                                )}
                            </button>
                        ))}
                        <form
                            onSubmit={(e) => { e.preventDefault(); load(); }}
                            className="relative ml-auto"
                        >
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search name / GSTIN / email"
                                className="w-72 rounded-full border border-zinc-200 bg-white pl-8 pr-3 py-1.5 text-xs outline-none focus:border-brand-400"
                                data-testid="admin-b2b-accounts-search"
                            />
                        </form>
                    </div>

                    {loading ? (
                        <div className="h-48 skeleton rounded-2xl" />
                    ) : items.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-b2b-accounts-empty">
                            <Building2 className="mx-auto h-8 w-8 text-slate-300" />
                            <p className="mt-2">No accounts in this view.</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                            <table className="w-full text-sm" data-testid="admin-b2b-accounts-table">
                                <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                    <tr>
                                        <th className="px-4 py-3">Organisation</th>
                                        <th className="px-4 py-3">GSTIN</th>
                                        <th className="px-4 py-3">Type</th>
                                        <th className="px-4 py-3">Tier · Limit</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Submitted</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-50">
                                    {items.map((a) => (
                                        <tr key={a.id} className="hover:bg-zinc-50" data-testid={`admin-b2b-account-row-${a.id}`}>
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-zinc-900">{a.legal_name}</p>
                                                <p className="text-xs text-slate-500">{a.primary_contact_name} · {a.phone}</p>
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs">{a.gstin}</td>
                                            <td className="px-4 py-3 text-xs capitalize">{(a.organization_type || "").replace("_", " ")}</td>
                                            <td className="px-4 py-3 text-xs">
                                                {a.status === "approved" ? (
                                                    <>
                                                        <p className="font-medium">{a.discount_tier || "—"}</p>
                                                        <p className="text-slate-500">{inr(a.credit_limit)} · N{a.credit_terms_days}</p>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[a.status] || "bg-zinc-100 text-zinc-700"}`}>{a.status.replace("_", " ")}</span>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button onClick={() => openDetail(a.id)} className="text-xs text-brand-700 hover:underline" data-testid={`admin-b2b-open-${a.id}`}>Open →</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Detail drawer */}
                {active && (
                    <div className="fixed inset-0 z-50 flex justify-end bg-zinc-900/40 backdrop-blur-sm" data-testid="admin-b2b-drawer">
                        <button onClick={() => { setActive(null); setMode(null); }} className="flex-1" aria-label="Close" />
                        <aside className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl">
                            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white px-5 py-4">
                                <div>
                                    <p className="label-uppercase">Account</p>
                                    <h2 className="text-base font-semibold">{active.legal_name}</h2>
                                </div>
                                <button onClick={() => { setActive(null); setMode(null); }} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100" data-testid="admin-b2b-drawer-close"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="space-y-4 p-5 text-sm">
                                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[active.status]}`}>{active.status.replace("_", " ")}</span>

                                <dl className="grid grid-cols-2 gap-3">
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">GSTIN</dt><dd className="font-mono text-xs">{active.gstin}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Type</dt><dd className="capitalize text-xs">{(active.organization_type || "").replace("_", " ")}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Drug Licence</dt><dd className="font-mono text-xs">{active.drug_license_no}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Licence expiry</dt><dd className="text-xs">{active.license_expiry_date}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Contact</dt><dd className="text-xs">{active.primary_contact_name}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Phone</dt><dd className="text-xs">{active.phone}</dd></div>
                                </dl>

                                {active.address && (
                                    <div className="rounded-xl bg-zinc-50 p-3 text-xs">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Registered address</p>
                                        <p className="mt-1 text-slate-700">{active.address.line1}{active.address.line2 ? `, ${active.address.line2}` : ""}</p>
                                        <p className="text-slate-700">{active.address.city}, {active.address.state} - {active.address.pincode}</p>
                                    </div>
                                )}

                                {active.status === "approved" && (
                                    <div className="rounded-xl border border-brand-100 bg-brand-50 p-3 text-xs">
                                        <p className="font-semibold text-brand-800">{active.discount_tier?.toUpperCase()} · Net {active.credit_terms_days}</p>
                                        <p className="mt-1">Credit limit: <strong>{inr(active.credit_limit)}</strong></p>
                                        <p>Outstanding: {inr(active.current_outstanding)}</p>
                                        <p className="mt-1 text-[10px] text-slate-500">{active.orders_count} orders · {active.invoices_count} invoices</p>
                                    </div>
                                )}

                                {active.status === "rejected" && active.rejected_reason && (
                                    <div className="rounded-xl bg-rose-50 p-3 text-xs text-rose-700">
                                        <p className="font-semibold">Rejection reason</p>
                                        <p className="mt-1">{active.rejected_reason}</p>
                                    </div>
                                )}

                                {/* Action buttons */}
                                {!mode && (
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {active.status === "pending_approval" && (
                                            <>
                                                <button onClick={() => openMode("approve")} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800" data-testid="admin-b2b-action-approve">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                                                </button>
                                                <button onClick={() => openMode("reject")} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50" data-testid="admin-b2b-action-reject">
                                                    <X className="h-3.5 w-3.5" /> Reject
                                                </button>
                                            </>
                                        )}
                                        {active.status === "approved" && (
                                            <>
                                                <button onClick={() => openMode("edit")} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="admin-b2b-action-edit">
                                                    <Edit3 className="h-3.5 w-3.5" /> Edit limit / tier
                                                </button>
                                                <button onClick={() => openMode("suspend")} className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50" data-testid="admin-b2b-action-suspend">
                                                    <Ban className="h-3.5 w-3.5" /> Suspend
                                                </button>
                                            </>
                                        )}
                                        {active.status === "suspended" && (
                                            <button onClick={reactivate} disabled={submitting} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-b2b-action-reactivate">
                                                <RotateCcw className="h-3.5 w-3.5" /> Reactivate
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Inline form */}
                                {(mode === "approve" || mode === "edit") && (
                                    <div className="space-y-3 rounded-xl border border-brand-200 bg-brand-50/40 p-4" data-testid="admin-b2b-approve-form">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-800">{mode === "approve" ? "Approve account" : "Edit account"}</p>
                                        <label className="block text-xs">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">Discount tier</span>
                                            <select value={form.discount_tier} onChange={(e) => setForm({ ...form, discount_tier: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-b2b-form-tier">
                                                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </label>
                                        <label className="block text-xs">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">Credit limit (₹)</span>
                                            <input type="number" min="0" step="1000" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-b2b-form-limit" />
                                        </label>
                                        <label className="block text-xs">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">Credit terms</span>
                                            <select value={form.credit_terms_days} onChange={(e) => setForm({ ...form, credit_terms_days: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-b2b-form-terms">
                                                {TERMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </label>
                                        <label className="flex items-center gap-2 text-xs">
                                            <input type="checkbox" checked={form.has_hospital_certificate} onChange={(e) => setForm({ ...form, has_hospital_certificate: e.target.checked })} data-testid="admin-b2b-form-hospcert" />
                                            <span>Hospital certificate verified (skip per-order Rx)</span>
                                        </label>
                                    </div>
                                )}

                                {(mode === "reject" || mode === "suspend") && (
                                    <div className="space-y-3 rounded-xl border border-rose-200 bg-rose-50/40 p-4">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-rose-800">{mode === "reject" ? "Reject application" : "Suspend account"}</p>
                                        <label className="block text-xs">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">Reason (visible to buyer)</span>
                                            <textarea rows={3} value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Drug licence expired" className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-xs" data-testid="admin-b2b-form-reason" />
                                        </label>
                                    </div>
                                )}

                                {mode && (
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button onClick={() => setMode(null)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs hover:bg-zinc-50">Cancel</button>
                                        <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-b2b-submit">
                                            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                            {submitting ? "Working…" : "Submit"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminB2BAccounts;
